/**
 * TTS 配音 API —— 对接 task.wancaiinfo.com
 *
 * 开发环境通过 Vite proxy (/ttsapi → https://task.wancaiinfo.com) 转发，
 * 生产环境直连 https://task.wancaiinfo.com。
 */

import { AxiosMode, type ApiResponse } from '@/axiosModel';
import { pollTask, type TaskSnapshot } from '@/api/storybook';
import axios from 'axios';

const apis = new AxiosMode();
const API = AxiosMode.TTS_API_HOST;
const AIPICTUREBOOK_API_BASE = `${AxiosMode.API_HOST}/api/aipicturebook`;
const DEFAULT_APP = 'microvideo-online';

export interface TtsService {
  name: string;
  code: string;
  deduction: string;
  notice: string | null;
  has_expire_role: boolean;
  more_role_link?: string;
}

export interface TtsRoleEmotion {
  name: string;
  code: string;
}

export interface TtsRoleProfile {
  auditionText: string;
  auditionUrl: string;
  sync?: boolean;
}

export interface TtsRole {
  id: string;
  name: string;
  gender: number;
  age: number;
  description: string;
  portrait: string;
  profile: TtsRoleProfile;
  tag: string | null;
  tagType: string | null;
  roleEmotion?: TtsRoleEmotion[];
  language?: string[];
}

export interface TtsRoleGroup {
  name: string;
  list: TtsRole[];
}

export type TtsRoleSource = 'aliyun' | 'data-baker' | 'azure' | 'xfyun';

export const DEFAULT_TTS_PROVIDER: TtsRoleSource = 'aliyun';

export interface TtsVoiceOption {
  id: string;
  name: string;
  description: string;
  portrait: string;
  provider: string;
  category: string;
  tag?: string | null;
  tagType?: string | null;
  profile: {
    auditionText: string;
    auditionUrl: string;
  };
}

function mapTtsRoleToVoiceOption(
  role: TtsRole,
  provider: string,
  category: string
): TtsVoiceOption {
  return {
    id: role.id,
    name: role.name,
    description: role.description,
    portrait: role.portrait,
    provider,
    category,
    tag: role.tag,
    tagType: role.tagType,
    profile: {
      auditionText: role.profile.auditionText,
      auditionUrl: role.profile.auditionUrl
    }
  };
}

export async function fetchDefaultTtsVoice(
  provider: TtsRoleSource = DEFAULT_TTS_PROVIDER
): Promise<TtsVoiceOption | null> {
  const groups = await fetchTtsRoles(provider);
  for (const group of groups) {
    const role = group.list?.[0];
    if (role) {
      return mapTtsRoleToVoiceOption(role, provider, group.name);
    }
  }
  return null;
}

export async function fetchTtsServices(app = DEFAULT_APP): Promise<TtsService[]> {
  const { req } = apis.post({
    postData: {},
    remotePath: `${API}/api/tts/services?app=${encodeURIComponent(app)}`,
    axiosRequestConfig: {
      headers: { 'Content-Type': 'application/json' }
    }
  });
  const res = (await req) as ApiResponse<TtsService[]>;
  return Array.isArray(res.data) ? res.data : [];
}

export async function fetchTtsRoles(source: string): Promise<TtsRoleGroup[]> {
  const { req } = apis.post({
    postData: { source },
    remotePath: `${API}/api/tts/role`,
    axiosRequestConfig: {
      headers: { 'Content-Type': 'application/json' }
    }
  });
  const res = (await req) as ApiResponse<TtsRoleGroup[]>;
  return Array.isArray(res.data) ? res.data : [];
}

export interface CreateTtsVoice {
  roleId: string;
  source: string;
  pitchRate: number;
  speed: number;
  volume: number;
  /** 配音角色名称 */
  name: string;
  /** 配音角色描述 */
  description?: string;
  /** 配音角色头像 */
  portrait?: string;
  /** 配音角色分类 */
  category?: string;
}

export interface CreateTtsPage {
  page: number;
  text: string;
}

/** 重试时成功页可用 null 占位，保持与原 pages 顺序一致 */
export type CreateTtsPageInput = CreateTtsPage | null;

export interface CreateTtsPayload {
  id: string;
  voice: CreateTtsVoice;
  pages: CreateTtsPageInput[];
}

export interface CreateTtsSubmitResult {
  taskId: string;
}

export interface TtsAudioItem {
  page: number;
  text: string;
  url: string;
}

export type TtsAudioList = TtsAudioItem[];

export interface TtsTaskResult {
  status: string;
  progress: number;
  audio: TtsAudioList;
  failed_pages: number[];
}

export interface CreateTtsResult {
  taskId: string;
  audio: TtsAudioList;
  failed_pages: number[];
  task: TaskSnapshot<TtsTaskResult>;
}

const unwrapAipicturebookData = <T>(response: unknown): T => {
  if (response && typeof response === 'object' && 'code' in response) {
    const apiResponse = response as ApiResponse<T>;
    if (apiResponse.code !== 'OK') throw new Error(apiResponse.msg || '请求失败');
    return apiResponse.data;
  }

  if (response && typeof response === 'object' && 'data' in response) {
    return (response as ApiResponse<T>).data;
  }

  return response as T;
};

export async function submitCreateTts(payload: CreateTtsPayload): Promise<CreateTtsSubmitResult> {
  const res = await axios.post(`${AIPICTUREBOOK_API_BASE}/create-tts`, payload, {
    headers: { 'Content-Type': 'application/json' }
  });
  const data = unwrapAipicturebookData<CreateTtsSubmitResult & { task_id?: string }>(res.data);
  const taskId = data.taskId || data.task_id || '';
  if (!taskId) throw new Error('TTS 任务创建失败');
  return { taskId };
}

export async function createTts(
  payload: CreateTtsPayload,
  options: {
    intervalMs?: number;
    timeoutMs?: number;
    onUpdate?: (task: TaskSnapshot<TtsTaskResult>) => void;
  } = {}
): Promise<CreateTtsResult> {
  const { taskId } = await submitCreateTts(payload);
  const task = await pollTask<TtsTaskResult>(taskId, options);
  const result = task.status === 'succeeded' ? task.result : null;

  return {
    taskId,
    audio: Array.isArray(result?.audio) ? result.audio : [],
    failed_pages: result?.failed_pages ?? [],
    task
  };
}

/** 背景音乐配置（写入 project-config.json 的 config.music） */
export interface ProjectMusicConfig {
  model: string;
  name: string;
  url: string;
  volume: number;
}

export interface CreateMusicPayload {
  id: string;
  url: string;
  name: string;
  volume: number;
  model?: string;
}

export interface CreateMusicResult {
  music: ProjectMusicConfig;
}

/**
 * 创建背景音乐配置：前端传入音乐文件 URL，后端下载后转存 OSS，并写入项目配置。
 */
export async function createMusic(payload: CreateMusicPayload): Promise<CreateMusicResult> {
  const res = await axios.post(
    `${AIPICTUREBOOK_API_BASE}/create-music`,
    {
      model: '',
      ...payload
    },
    {
      headers: { 'Content-Type': 'application/json' }
    }
  );
  return unwrapAipicturebookData<CreateMusicResult>(res.data);
}
